import fullDate from '@/filters/fullDate';
import { trans } from '@/mixins/i18n';
import { DateTime } from 'luxon';
import { Timezone } from '../../Utils/timezone';

const colors = {
    asset_check: {
        bg: 'bg-[#E1ECFF]',
        text: 'text-[#92AEDD]',
    },
    preventative_maintenance: {
        bg: 'bg-green-light',
        text: 'text-[#A2BD99]',
    },
    performance_data: {
        bg: 'bg-[#E4E9F9]',
        text: 'text-[#8E97B6]',
    },
    captured: {
        bg: 'bg-[#E4E9F9]',
        text: 'text-[#8E97B6]',
    },
    confirmed: {
        bg: 'bg-[#E1ECFF]',
        text: 'text-[#92AEDD]',
    },
    updated: {
        bg: 'bg-[#E1ECFF]',
        text: 'text-[#92AEDD]',
    },
    successful: {
        bg: 'bg-[#E8F5E4]',
        text: 'text-[#A2BD99]',
    },
    failed: {
        bg: 'bg-[#FAE6E7]',
        text: 'text-[#E48586]',
    },
    overdue: {
        bg: 'bg-[#F2F5FA]',
        text: 'text-[#525252]',
    },
};

export function getTaskResolutionColor(taskResolution: string, prefix: string = 'bg'): string {
    if (!taskResolution || !colors[taskResolution]) {
        return '';
    }

    return colors[taskResolution][prefix];
}

export function getTaskGroupColor(taskGroup: string, prefix: string = 'bg'): string {
    if (!colors[taskGroup]) {
        return '';
    }

    return colors[taskGroup][prefix];
}

export function getTaskStatusUpdatedAt(task, locale = 'en'): string {
    if (!task) {
        return;
    }

    return new Intl.DateTimeFormat('en-US', {
        timeZone: task.asset?.timezone?.identifier ?? 'utc',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).format(new Date(task.resolved_at ?? task.due_at));
}

export function determineTaskResolution(item): undefined | boolean {
    if (!item) {
        return;
    }

    if (item?.resolution) {
        return ['captured', 'confirmed', 'updated', 'successful'].includes(item.resolution.value);
    }

    if (item?.resolved_at) {
        return true;
    }

    if (item?.is_overdue) {
        return false;
    }
}

export function getDate(item, locale: string): string | undefined {
    if (!item) {
        return;
    }

    const dateString = item.due_at;

    if (!dateString) {
        return '-';
    }

    // Render the date in the asset's location timezone so it stays consistent with the
    // time shown beside it (getTaskStatusUpdatedAt), instead of drifting to the browser's zone.
    // Fall back to 'utc' to match that sibling when the asset has no timezone identifier.
    const zone = item.asset?.timezone?.identifier ?? 'utc';

    let date = DateTime.fromISO(dateString, { zone });

    // Check if date is valid
    if (!date || !date.isValid) {
        return '-';
    }

    date = date.setLocale(locale);

    const now = DateTime.now().setZone(zone);
    if (date.hasSame(now, 'day')) {
        return trans('general.today');
    }

    return fullDate(date, locale, zone);
}

export function getTimeZoneInfo(item, locale = 'en'): string | undefined {
    if (!item) {
        return;
    }

    return Timezone.fromISO(item.resolved_at ?? item.due_at, locale).shortName;
}
